import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import DynamicDataCollectionForm from '../components/DynamicDataCollectionForm';
import MapEngine from '../components/MapEngine';
import { getDataType, hasPermission } from '../config/dataTypes';
import { API_URL } from '../components/config.js';
import axios from 'axios';
import './DataCollectionPage.css';

/**
 * DataCollectionPage Component
 * 
 * Unified page for all data collection types.
 * Uses route parameter to determine which data type to display.
 * 
 * Routes:
 * - /data/berry-harvesting
 * - /data/moose-sightings
 * - /data/environmental-hazards
 * - /data/consultation (migrated from old system)
 */
const DataCollectionPage = () => {
  const { dataTypeId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useContext(AuthContext);

  const [dataTypeConfig, setDataTypeConfig] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list', 'map', 'form'
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingItem, setEditingItem] = useState(null);

  // Load data from backend
  const loadData = useCallback(async (config) => {
    setLoading(true);
    setError('');

    try {
      // Use unified API endpoint with data type parameter
      const response = await axios.get(`${API_URL}/api/data/${config.id}`, {
        headers: currentUser?.token ? { Authorization: `Bearer ${currentUser.token}` } : {}
      });

      setData(response.data || []);
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.response?.data?.error || 'Error loading data');
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [currentUser?.token]);

  // Load data type configuration
  useEffect(() => {
    const config = getDataType(dataTypeId);
    if (!config) {
      setError(`Invalid data type: ${dataTypeId}`);
      setLoading(false);
      return;
    }

    // Check view permission
    const userRole = currentUser?.role || 'public';
    if (!hasPermission(config, 'view', userRole)) {
      setError('You do not have permission to view this data');
      setLoading(false);
      return;
    }

    setDataTypeConfig(config);
    loadData(config);
  }, [dataTypeId, currentUser, loadData]);

  // Handle form submission (create or update)
  const handleSubmit = async (formData, isFormData) => {
    try {
      const url = editingItem
        ? `${API_URL}/api/data/${dataTypeConfig.id}/${editingItem._id}`
        : `${API_URL}/api/data/${dataTypeConfig.id}`;

      const method = editingItem ? 'put' : 'post';

      const config = {
        headers: {
          Authorization: `Bearer ${currentUser.token}`,
          ...(isFormData ? {} : { 'Content-Type': 'application/json' })
        }
      };

      await axios[method](url, formData, config);

      // Reload data
      await loadData(dataTypeConfig);

      // Return to list view
      setViewMode('list');
      setEditingItem(null);
    } catch (err) {
      console.error('Error submitting data:', err);
      throw new Error(err.response?.data?.error || 'Error submitting data');
    }
  };

  // Handle delete
  const handleDelete = async (item) => {
    if (!window.confirm('Are you sure you want to delete this item?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/data/${dataTypeConfig.id}/${item._id}`, {
        headers: { Authorization: `Bearer ${currentUser.token}` }
      });

      // Reload data
      await loadData(dataTypeConfig);
    } catch (err) {
      console.error('Error deleting item:', err);
      setError(err.response?.data?.error || 'Error deleting item');
    }
  };

  // Handle edit
  const handleEdit = (item) => {
    setEditingItem(item);
    setViewMode('form');
  };

  // Handle new item
  const handleNew = () => {
    // Check create permission
    const userRole = currentUser?.role || 'public';
    if (!hasPermission(dataTypeConfig, 'create', userRole)) {
      setError('You do not have permission to create new items');
      return;
    }

    setEditingItem(null);
    setViewMode('form');
  };

  // Cancel form
  const handleCancel = () => {
    setEditingItem(null);
    setViewMode('list');
  };

  if (loading) {
    return (
      <div className="data-collection-page loading">
        <div className="spinner"></div>
        <p>Loading {dataTypeConfig?.name || 'data'}...</p>
      </div>
    );
  }

  if (error && !dataTypeConfig) {
    return (
      <div className="data-collection-page error">
        <div className="error-message">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/')} className="btn btn-primary">
            Return Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="data-collection-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-content">
          <h1>
            {dataTypeConfig.icon} {dataTypeConfig.name}
          </h1>
          <p className="page-description">{dataTypeConfig.description}</p>
        </div>
        
        {/* View Controls */}
        <div className="view-controls">
          <div className="view-toggle">
            <button
              className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
            >
              📋 List
            </button>
            <button
              className={`view-btn ${viewMode === 'map' ? 'active' : ''}`}
              onClick={() => setViewMode('map')}
            >
              🗺️ Map
            </button>
          </div>
          
          {viewMode !== 'form' && hasPermission(dataTypeConfig, 'create', currentUser?.role || 'public') && (
            <button onClick={handleNew} className="btn btn-primary">
              + New {dataTypeConfig.name.replace(/s$/, '')}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="alert alert-danger">
          {error}
        </div>
      )}

      {/* Content Area */}
      <div className="page-content">
        {viewMode === 'form' && (
          <DynamicDataCollectionForm
            dataTypeConfig={dataTypeConfig}
            initialData={editingItem}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
          />
        )}

        {viewMode === 'list' && (
          <div className="data-list">
            {data.length === 0 ? (
              <div className="empty-state">
                <p>No {dataTypeConfig.name.toLowerCase()} yet.</p>
                {hasPermission(dataTypeConfig, 'create', currentUser?.role || 'public') && (
                  <button onClick={handleNew} className="btn btn-primary">
                    Create First Entry
                  </button>
                )}
              </div>
            ) : (
              <div className="data-grid">
                {data.map(item => (
                  <div key={item._id} className="data-card">
                    <div className="card-icon">
                      {item.structuredData?.selectedIcon?.icon || dataTypeConfig.icon}
                    </div>
                    <div className="card-content">
                      <h3>
                        {item.structuredData?.title || 
                         item.title || 
                         item.structuredData?.selectedIcon?.label || 
                         dataTypeConfig.name}
                      </h3>
                      {item.description && (
                        <p className="card-description">
                          {item.description.substring(0, 150)}
                          {item.description.length > 150 ? '...' : ''}
                        </p>
                      )}
                      <div className="card-meta">
                        <span>By: {item.createdByName || 'Anonymous'}</span>
                        <span>{new Date(item.createdAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div className="card-actions">
                      {hasPermission(dataTypeConfig, 'edit', currentUser?.role, item.createdBy === currentUser?._id) && (
                        <button onClick={() => handleEdit(item)} className="btn-icon" title="Edit">
                          ✏️
                        </button>
                      )}
                      {hasPermission(dataTypeConfig, 'delete', currentUser?.role, item.createdBy === currentUser?._id) && (
                        <button onClick={() => handleDelete(item)} className="btn-icon" title="Delete">
                          🗑️
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {viewMode === 'map' && (
          <MapEngine
            dataLayers={[
              {
                dataTypeId: dataTypeConfig.id,
                data: data
              }
            ]}
            height="calc(100vh - 250px)"
            onFeatureClick={(item) => {
              console.log('Feature clicked:', item);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default DataCollectionPage;
